import React, { createContext } from "react";
import {
  GenericFieldError,
  deepInsert,
} from "@common/validation/entityValidationUtils";
import { BackendDeviceValidation } from "@services/devices";
import { v4 as uuid } from "uuid";

export enum IImportMode {
  /**
   * Should only be present in DeviceProvisioning
   */
  PROVISIONING = "PROVISIONING",
  EXCEL = "EXCEL",
  MANUAL = "MANUAL",
  BULK_UPLOAD = "BULK_UPLOAD",
}

export const ImportMode: { [k in IImportMode]: k } = {
  [IImportMode.EXCEL]: IImportMode.EXCEL,
  [IImportMode.MANUAL]: IImportMode.MANUAL,
  [IImportMode.BULK_UPLOAD]: IImportMode.BULK_UPLOAD,
  [IImportMode.PROVISIONING]: IImportMode.PROVISIONING,
};
export interface BreadcrumbProvisionningProps {
  previous: string;
  title?: string;
  label?: string;
}
export type MappingField<T> = {
  key: keyof T;
  excelField: string;
};

export type OpenWithDataProps<T> = {
  excelFieldsList: string[];
  rawData: string[][];
  /**
   * if undefined, the import list will use the stored mapping, and guess how to apply it to the file currently sent
   */
  mapping?: MappingField<T>[];
  manualImport?: boolean;
};

export type IdentifiedMappingField<T> = Partial<
  Omit<MappingField<T>, "key">
> & {
  key: keyof T;
  id: string;
};
// Used to manage the fact that two columns can have the same "key" from the device, but still have different values stored within
export type DataCellRetention<T> = {
  [k in IdentifiedMappingField<T>["id"]]: Pick<
    IdentifiedMappingField<T>,
    "key"
  > & {
    data: {
      [u: string]: any;
    };
  };
};
export type DataRetentionEntry<T> = {
  value: T[keyof T];
} & Pick<IdentifiedMappingField<T>, "key" | "id">;
export enum ProvisioningFilters {
  INVALID_DATA,
  EMPTY_REQUIRED,
}
export interface IImportContext<T> {
  open: boolean;
  breadcrumb: BreadcrumbProvisionningProps;
  excelHeaders?: string[];
  excelRawData?: string[][];
  mapping?: MappingField<T>[];
}

export interface IImportDispatchContext<T> {
  openWithData: (data: OpenWithDataProps<T>, importMode?: IImportMode) => void;
  onConfirm: () => void;
  setOpen: (b: boolean) => void;
}
export interface IImportListContext<
  T,
  E extends GenericFieldError<unknown, unknown>,
  K extends string | number = number
> {
  displayMapping: boolean;
  showUnmapped: boolean;
  displayBulkRow: boolean;
  floatingData: DataCellRetention<T>;
  mapping?: IdentifiedMappingField<T>[];
  selected?: Array<K>;
  backendErrors: { [k in K]: E };
  filter?: ProvisioningFilters;
  page: number;
  importMode?: IImportMode;
  /**
   * Having these two booleans as purely computed values to have more
   * readable conditions throughout the code i.e.
   * `if(manualImport)` instead of `if(importMode === ImportMode.MANUAL)`
   */
  manualImport: boolean;
  bulkUpload: boolean;
}
export interface IImportListDispatchContext<
  T,
  R extends {},
  E extends GenericFieldError<unknown, unknown>,
  K extends string | number = number
> {
  setBackendErrors: (be: BackendDeviceValidation[]) => void;
  setDisplayMapping: (b: boolean) => void;
  setShowUnmapped: (b: boolean) => void;
  setDisplayBulkRow: (b: boolean) => void;
  setFilter: (f: ProvisioningFilters | undefined) => void;
  setSelected: (s: IImportListContext<T, E>["selected"]) => void;
  setMappings: (newMapping: MappingField<T>[]) => void;
  setMapping: (
    identifier: string,
    colKey: keyof T,
    excelField?: string
  ) => void;
  setIdentifiedMappings: (newMapping: IdentifiedMappingField<T>[]) => void;
  getErrors: (id: K, property: keyof T) => E[];
  bulkChange: <L extends keyof T>(
    ids: Array<K>,
    changes: Partial<T>,
    dataEntry?: DataRetentionEntry<T>
  ) => void;
  setValue: <L extends keyof T>(
    id: K,
    compositeKey: L,
    value: T[L],
    dataEntry?: DataRetentionEntry<T>
  ) => void;
  setValues: (
    ids: K,
    changes: Partial<T>,
    dataEntry?: DataRetentionEntry<T>
  ) => void;
  addRows: (rows: R[]) => void;
  removeRows: () => void;
  setPage: (page: number) => void;
}

export const createImportContext = <C,>(initialValue: IImportContext<C>) =>
  createContext<IImportContext<C>>(initialValue);
export const createImportDispatchContext = <C,>(
  initialValue: IImportDispatchContext<C>
) => createContext<IImportDispatchContext<C>>(initialValue);
export const createImportListContext = <
  C,
  E extends GenericFieldError<unknown, unknown>,
  T extends {}
>(
  initialValue: IImportListContext<C, E> & T
) => createContext<IImportListContext<C, E> & T>(initialValue);
export const createImportListDispatchContext = <
  C,
  E extends GenericFieldError<unknown, unknown>,
  R extends {}
>(
  initialValue: IImportListDispatchContext<C, R, E>
) => createContext<IImportListDispatchContext<C, R, E>>(initialValue);

export const getHeaderIndex = (
  excelFieldsList: string[],
  excelField: string
) => {
  return excelFieldsList.findIndex((header) => header === excelField) ?? -1;
};

const getBooleanFromString = (value?: string) => {
  const lowerCase = value?.toLocaleLowerCase();
  if (lowerCase === "yes" || lowerCase === "true") {
    return true;
  } else {
    return false;
  }
};

const getDateFromString = (value: string) =>
  value ? new Date(value).getTime() : value;
export const getValuesFromRawData = <T,>(
  excelFieldsList: string[],
  rawData: string[][],
  { key, excelField }: MappingField<T>,
  columns: { type?: "boolean" | string; key: any }[]
) => {
  const headerIndex = getHeaderIndex(excelFieldsList, excelField);

  if (headerIndex < 0) return undefined;
  return rawData.map((values) => {
    const column = columns.find((x) => x.key === key);
    const columnType = column?.type;

    if (column && columnType === "boolean") {
      return getBooleanFromString(values[headerIndex]);
    }
    if (column && columnType === "date") {
      return getDateFromString(values[headerIndex]);
    }
    return values[headerIndex]?.trim();
  });
};

export const constructFloatingData = <T,>(
  excelFieldList: string[],
  rawData: string[][],
  mapping: MappingField<T>,
  columns: { type?: "boolean" | string; key: any }[]
) =>
  (
    getValuesFromRawData(excelFieldList, rawData, mapping, columns) ?? []
  ).reduce((acc, value, index) => ({ ...acc, [index]: value }), {});

export const constructEntityFromFloatingData = <F, T extends object>(
  floatingData: DataCellRetention<F>,
  id: string
): T => {
  let temp: T = {} as T;
  Object.entries(floatingData)
    .map(([_, entry]) => ({
      key: entry.key,
      data: entry.data[id],
    }))
    .forEach(({ key, data }) => (temp = deepInsert(temp, key as string, data)));

  return temp;
};

export const constructMappingFromMappingAndChanges = <
  T extends object,
  K extends T
>(
  changes: T,
  mapping: IdentifiedMappingField<K>[]
) => {
  let temp: IdentifiedMappingField<K>[] = [...mapping];

  Object.keys(changes).forEach((k) => {
    if (isIndexColumn(k)) return;
    const matchMapping = temp.find((m) => m.key === k);
    if (matchMapping === undefined) {
      temp.push({ key: k as keyof K, id: uuid() });
    }
  });

  return temp;
};

export const constructMappingFromMappingAndEntities = <
  T extends object & { index?: string },
  K extends T
>(
  entities: T[],
  mapping: IdentifiedMappingField<K>[]
) => {
  let temp: IdentifiedMappingField<K>[] = [...mapping];

  entities.forEach((e, index) => {
    Object.keys(e).forEach((k) => {
      if (isIndexColumn(k)) return;
      const matchMapping = temp.find((m) => m.key === k);
      if (matchMapping === undefined) {
        temp.push({ key: k as keyof K, id: uuid() });
      }
    });
  });

  return temp;
};

export const constructFloatingDataFromMappingAndEntities = <
  T extends object & { index?: string },
  K extends T
>(
  entities: T[],
  mapping: IdentifiedMappingField<K>[]
) => {
  let temp: DataCellRetention<K> = {};
  mapping.forEach((m) => {
    temp[m.id] = { key: m.key, data: {} };
    entities.forEach((e, index) => {
      Object.keys(e).forEach((k) => {
        if (k === m.key) {
          Object.assign(temp[m.id].data, {
            [e.index ?? index]: e[k as keyof T],
          });
        }
      });
    });
  });

  return temp;
};

export const constructEmptyFloatingData = <
  T extends object & { index?: string },
  K extends T
>(
  mapping: IdentifiedMappingField<K>[]
) => {
  let temp: DataCellRetention<K> = {};
  mapping.forEach((m) => {
    temp[m.id] = { key: m.key, data: {} };
  });
  return temp;
};

export const constructEntitiesFromFloatingData = <
  F,
  T extends { index: string | number }
>(
  floatingData: DataCellRetention<F>,
  entities?: T[]
): T[] => {
  let temp: T[] = entities ?? [];

  temp.forEach((t) => {
    Object.keys(t).forEach((k) => k !== "index" && delete t[k as keyof T]);
  });

  Object.entries(floatingData).forEach(([_, { key, data }]) => {
    Object.entries(data).forEach(([index, value]) => {
      let found = temp.find((x) => `${x.index}` === index);
      if (!found) temp.push(deepInsert({ index }, key as string, value) as T);
      else
        temp = temp.map((x) =>
          `${x.index}` === index ? deepInsert(x, key as string, value) : x
        );
    });
  });

  return temp;
};

export const constructMappingFromMappingAndErrors = <
  T extends {},
  E extends GenericFieldError<unknown, unknown>
>(
  errors: E[],
  mapping: IdentifiedMappingField<T>[]
): IdentifiedMappingField<T>[] => {
  let temp: IdentifiedMappingField<T>[] = [...(mapping ?? [])];
  errors.forEach((e) => {
    if (typeof e.property === "string") {
      const matchMapping = temp.find((m) => {
        return m.key === e.property;
      });
      if (matchMapping === undefined)
        temp.push({ id: uuid(), key: e.property as keyof T });
    } else {
      (e.property as string[]).forEach((p) => {
        const matchMapping = temp.find((m) => {
          return m.key === p;
        });
        if (matchMapping === undefined)
          temp.push({ id: uuid(), key: p as keyof T });
      });
    }
  });
  return temp;
};

// just used to discriminate against index columns to easily filter them out
export const isIndexColumn = (
  colKey: string,
  checkComposedKey: boolean = false
) => {
  if (colKey === "index") return true;
  if (!checkComposedKey) return false;
  const split = colKey.split(".");
  return split[split.length - 1] === "index";
};
